<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import type { CatalogueItem } from '@/models/Catalogue';
import { useCataloguePermissions } from '@/composables/catalogue/useCataloguePermissions';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';

const { $gettext } = gettext;
const { canEditItem } = useCataloguePermissions();
const actions = inject(CatalogueItemActionsKey)!;

const props = defineProps<{
  item: CatalogueItem;
  files: string[];
}>();

const canEdit = computed(() => {
  return canEditItem(props.item);
});

const fileInput = ref<HTMLInputElement | null>(null);
const uploadProgress = ref(0);
const showDeleteDialog = ref(false);
const fileToDelete = ref<string>('');
const showFileSizeError = ref(false);

const maxFileSize = 15 * 1024 * 1024; // 15MB

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    if (target.files[0].size > maxFileSize) {
      showFileSizeError.value = true;
      // Clear the input
      target.value = '';
    } else {
      actions.uploadFile(target.files[0]);
    }
  }
};

const handleFileDelete = (fileUrl: string) => {
  fileToDelete.value = fileUrl;
  showDeleteDialog.value = true;
};

const confirmFileDelete = () => {
  const filename = fileToDelete.value.split('/').pop();
  if (filename) {
    actions.deleteFile(filename);
  }
  showDeleteDialog.value = false;
  fileToDelete.value = '';
};

const cancelFileDelete = () => {
  showDeleteDialog.value = false;
  fileToDelete.value = '';
};

const handleFileDownload = (fileUrl: string) => {
  window.open(fileUrl, '_blank', 'noopener,noreferrer');
};
</script>

<template>
  <div>
    <div class="d-flex align-center mb-4">
      <h3 class="text-h6">{{ $gettext('Files') }}</h3>
      <v-spacer />
      <r-btn
        v-if="canEdit"
        icon="mdi-upload"
        variant="outlined"
        @click="($refs.fileInput as HTMLInputElement).click()"
      >
        {{ $gettext('Upload File') }}
      </r-btn>
      <v-file-input v-show="false" ref="fileInput" @change="handleFileSelect" />
    </div>

    <div>
      <v-progress-linear
        v-if="uploadProgress > 0"
        :model-value="uploadProgress"
        color="primary"
        class="mb-4"
      />

      <v-alert
        v-if="showFileSizeError"
        type="error"
        variant="tonal"
        closable
        class="mb-4"
        @click:close="showFileSizeError = false"
      >
        {{ $gettext('File size exceeds the maximum limit of 15MB.') }}
      </v-alert>

      <v-list v-if="files.length > 0">
        <v-list-item v-for="file in files" :key="file">
          <template #prepend>
            <v-icon>mdi-file</v-icon>
          </template>
          <v-list-item-title>{{
            (file.split('/').pop() || 'catalogue file').split('?')[0]
          }}</v-list-item-title>
          <template #append>
            <v-icon
              icon="mdi-download"
              class="cursor-pointer mr-2"
              @click="handleFileDownload(file)"
            />
            <v-icon
              v-if="canEdit"
              icon="mdi-delete"
              color="error"
              class="cursor-pointer"
              @click="handleFileDelete(file)"
            />
          </template>
        </v-list-item>
      </v-list>
      <v-alert
        v-else
        type="info"
        variant="tonal"
        :text="$gettext('No files available')"
      />
    </div>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $gettext('Delete File') }}</v-card-title>
        <v-card-text>
          {{
            $gettext(
              'Are you sure you want to delete this file? This action cannot be undone.',
            )
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey-darken-1" variant="text" @click="cancelFileDelete">
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-btn color="error" variant="text" @click="confirmFileDelete">
            {{ $gettext('Delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
